import {
  animate,
  group,
  query,
  style,
  transition,
  trigger,
} from '@angular/animations';
import { DOCUMENT, NgTemplateOutlet, isPlatformBrowser } from '@angular/common';
import type {
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
  TemplateRef,
} from '@angular/core';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostBinding,
  HostListener,
  Input,
  PLATFORM_ID,
  Output,
  QueryList,
  Renderer2,
  ViewChild,
  ViewChildren,
  inject,
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { TimeUtils } from '@freelancer/time-utils';
import { ButtonComponent } from '@freelancer/ui/button';
import { CardComponent, type CardBorderRadius } from '@freelancer/ui/card';
import { trackByValue } from '@freelancer/ui/helpers';
import { IconModule, IconColor, IconSize } from '@freelancer/ui/icon';
import {
  InputBackgroundColor,
  InputComponent,
  InputIconStart,
  InputSize,
  InputType,
} from '@freelancer/ui/input';
import {
  ListItemComponent,
  ListItemSelectedType,
} from '@freelancer/ui/list-item';
import { ScrollableContentComponent } from '@freelancer/ui/scrollable-content';
import { UserAgent } from '@freelancer/user-agent';
import type { Subscription } from 'rxjs';
import { SearchExpandFromDirection } from './search.types';
import type { GroupedSearchResults, SearchItem } from './search.types';
import { Focus } from '../focus/focus.service';
import { ListComponent } from '../list/list.component';

@Component({
  standalone: true,
  selector: 'fl-search',
  imports: [
    ButtonComponent,
    CardComponent,
    IconModule,
    InputComponent,
    ListComponent,
    ListItemComponent,
    NgTemplateOutlet,
    ScrollableContentComponent,
  ],
  template: `
    <div class="SearchRoot">
      <div class="SearchBody">
        <div class="InputWrapper">
          <fl-input
            #inputComponent
            class="Input"
            [backgroundColor]="backgroundColor"
            [borderless]="borderless"
            [borderlessMobile]="borderlessMobile"
            [dynamicValidation]="dynamicValidation"
            [control]="queryControl"
            [disabled]="disabled"
            [expanded]="expanded"
            [size]="inputSearchSize"
            [iconMarginMobile]="iconMarginMobile"
            [iconStart]="showIcon ? leftIcon : undefined"
            [iconStartLabel]="'Search'"
            [leftIconSize]="leftIconSize"
            [id]="inputId"
            [isExpandable]="isExpandable"
            [expandFromDirection]="expandFromDirection"
            [leftIconColor]="searchIconColor"
            [placeholder]="placeholder"
            [rightIconColor]="IconColor.FOREGROUND"
            [type]="InputType.SEARCH"
            [searchRoundCorners]="roundCorners"
            [shakeOnBlur]="shakeOnBlur"
            (iconStartClick)="handleIconStartClick($event)"
            (keyup)="handleInputKeyUp($event)"
            (keydown)="handleInputKeyDown($event)"
            (onFocus)="handleFocus()"
          ></fl-input>
          @if (
            !overlayMode &&
            (showShortcutKey || showEscHint) &&
            !queryControl.value
          ) {
            <div
              class="ShortcutHints"
              aria-hidden="true"
            >
              @if (showEscHint) {
                <span
                  class="ShortcutHint"
                  i18n
                >
                  Esc
                </span>
              }
              @if (showShortcutKey) {
                <span class="ShortcutHint">{{ shortcutHintText }}</span>
              }
            </div>
          }
          @if (!overlayMode && queryControl.value && clearButton) {
            <fl-button
              class="ButtonClear"
              [disabled]="disabled"
              (click)="handleIconClearClick($event)"
            >
              <fl-icon
                class="IconClear"
                [name]="'ui-close'"
                [size]="IconSize.SMALL"
                [useIconFont]="true"
                [fillIconFont]="true"
              ></fl-icon>
            </fl-button>
          }
        </div>

        @if (!overlayMode) {
          <ng-container *ngTemplateOutlet="resultsPanel"></ng-container>
        }
      </div>
    </div>

    @if (overlayMode) {
      <div
        class="SearchOverlay"
        [@overlayAnimation]
      >
        <div
          class="OverlayBackdrop"
          (click)="handleOverlayClose()"
        ></div>
        <div class="SearchOverlay-body">
          <div class="InputWrapper">
            <fl-input
              #overlayInputComponent
              class="Input"
              [backgroundColor]="backgroundColor"
              [control]="queryControl"
              [disabled]="disabled"
              [expanded]="true"
              [size]="inputSearchSize"
              [iconStart]="showIcon ? leftIcon : undefined"
              [iconStartLabel]="'Search'"
              [leftIconSize]="leftIconSize"
              [leftIconColor]="IconColor.FOREGROUND"
              [placeholder]="placeholder"
              [rightIconColor]="IconColor.FOREGROUND"
              [type]="InputType.SEARCH"
              [searchRoundCorners]="roundCorners"
              (keyup)="handleInputKeyUp($event)"
              (keydown)="handleInputKeyDown($event)"
              (onFocus)="handleFocus()"
            ></fl-input>
            @if ((showShortcutKey || showEscHint) && !queryControl.value) {
              <div
                class="ShortcutHints"
                aria-hidden="true"
              >
                @if (showEscHint) {
                  <span
                    class="ShortcutHint"
                    i18n
                  >
                    Esc
                  </span>
                }
                @if (showShortcutKey) {
                  <span class="ShortcutHint">{{ shortcutHintText }}</span>
                }
              </div>
            }
            @if (queryControl.value && clearButton) {
              <fl-button
                class="ButtonClear"
                [disabled]="disabled"
                (click)="handleOverlayClearClick($event)"
              >
                <fl-icon
                  class="IconClear"
                  [name]="'ui-close'"
                  [size]="IconSize.SMALL"
                  [useIconFont]="true"
                  [fillIconFont]="true"
                ></fl-icon>
              </fl-button>
            }
          </div>
          <ng-container *ngTemplateOutlet="resultsPanel"></ng-container>
          @if (showKeyboardHints) {
            <div class="KeyboardHintsBar">
              <div class="KeyboardHintsBar-item">
                <kbd
                  class="KeyboardHintsBar-key"
                  i18n
                  >Esc</kbd
                >
                <span
                  class="KeyboardHintsBar-label text-foreground"
                  i18n
                  >Close</span
                >
              </div>
              <div class="KeyboardHintsBar-item">
                <kbd
                  class="KeyboardHintsBar-key"
                  i18n
                  >↵</kbd
                >
                <span
                  class="KeyboardHintsBar-label text-foreground"
                  i18n
                  >Go to page</span
                >
              </div>
              @if (showNestedStories) {
                <div class="KeyboardHintsBar-item">
                  <kbd class="KeyboardHintsBar-key">{{
                    expandModifierHintText
                  }}</kbd>
                  <kbd
                    class="KeyboardHintsBar-key"
                    i18n
                    >↵</kbd
                  >
                  @if (isFocusedItemInExpandedGroup()) {
                    <span
                      class="KeyboardHintsBar-label text-foreground"
                      i18n
                      >Collapse</span
                    >
                  } @else {
                    <span
                      class="KeyboardHintsBar-label text-foreground"
                      i18n
                      >Expand</span
                    >
                  }
                </div>
              }
            </div>
          }
        </div>
      </div>
    }

    <ng-template #resultsPanel>
      @if (showResultsPanel()) {
        @if (useResultsCard) {
          <fl-card
            class="SearchResults"
            [class.SearchResults-inline]="resultsInFlow"
            [borderRadius]="resultsBorderRadius"
            [edgeToEdge]="true"
            [class.SearchResults-callout]="increaseZIndex"
          >
            @if (scrollableSearchResults) {
              <fl-scrollable-content
                class="ScrollbarContainer"
                [autoHeight]="true"
              >
                <ng-container *ngTemplateOutlet="searchResults"></ng-container>
              </fl-scrollable-content>
            } @else {
              <ng-container *ngTemplateOutlet="searchResults"></ng-container>
            }
          </fl-card>
        } @else {
          <div
            class="SearchResults SearchResultsPlain"
            [class.SearchResults-inline]="resultsInFlow"
            [class.SearchResults-callout]="increaseZIndex"
            [attr.data-radius]="resultsBorderRadius"
          >
            @if (scrollableSearchResults) {
              <fl-scrollable-content
                class="ScrollbarContainer"
                [autoHeight]="true"
              >
                <ng-container *ngTemplateOutlet="searchResults"></ng-container>
              </fl-scrollable-content>
            } @else {
              <ng-container *ngTemplateOutlet="searchResults"></ng-container>
            }
          </div>
        }
      }
    </ng-template>

    <ng-template #searchResults>
      @for (type of itemTypes; track trackByValue($index, type)) {
        @if (itemTypeTitles[type]) {
          <div class="GroupTitleRow">
            <p class="GroupTitle text-title-20">
              {{ itemTypeTitles[type] }}
            </p>
          </div>
        }
        <fl-list
          [bordered]="false"
          [bottomBorder]="false"
          [clickable]="true"
        >
          @for (
            item of groupedSearchResults[type];
            track trackBySearchItem(i, item);
            let i = $index
          ) {
            <fl-list-item
              #listItem
              [showAsSelected]="isParentSelected(groupOffsets[type] + i)"
              [selectedType]="ListItemSelectedType.FILLED"
              (mousemove)="mouseMove(listItem)"
              (click)="activateParentItem(item, groupOffsets[type] + i)"
              [attr.itemKind]="'parent'"
              [attr.itemType]="type"
              [attr.itemIndex]="i"
              [attr.parentIndex]="groupOffsets[type] + i"
            >
              <ng-container
                *ngTemplateOutlet="
                  itemTemplates[item.type];
                  context: {
                    $implicit: item.context,
                    selected: isParentExpanded(groupOffsets[type] + i),
                  }
                "
              ></ng-container>
            </fl-list-item>
            @if (
              showNestedStories &&
              isParentExpanded(groupOffsets[type] + i) &&
              hasNestedStories(item)
            ) {
              @for (story of getNestedStories(item); track story.link) {
                <fl-list-item
                  #listItem
                  class="NestedStoryItem"
                  [showAsSelected]="isNestedSelected(type, i, $index)"
                  [selectedType]="ListItemSelectedType.FILLED"
                  (mousemove)="mouseMove(listItem)"
                  (click)="selectNestedStory(item, story)"
                  [attr.itemKind]="'nested'"
                  [attr.itemType]="type"
                  [attr.itemIndex]="i"
                  [attr.storyIndex]="$index"
                  [attr.parentIndex]="groupOffsets[type] + i"
                >
                  <p class="NestedStoryLabel text-body-16">
                    <span class="NestedStoryParent">{{
                      getItemName(item)
                    }}</span>
                    <span class="NestedStorySeparator">›</span>
                    <span>{{ story.title }}</span>
                  </p>
                </fl-list-item>
              }
            }
          }
        </fl-list>
      }
    </ng-template>
  `,
  styleUrls: ['./search.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [
    trigger('overlayAnimation', [
      transition(':enter', [
        group([
          query('.OverlayBackdrop', [
            style({ opacity: 0 }),
            animate('100ms ease-out', style({ opacity: 1 })),
          ]),
          query('.SearchOverlay-body', [
            style({ opacity: 0 }),
            animate('100ms ease-out', style({ opacity: 1 })),
          ]),
        ]),
      ]),
      transition(':leave', [
        group([
          query('.OverlayBackdrop', [
            animate('100ms ease-in', style({ opacity: 0 })),
          ]),
          query('.SearchOverlay-body', [
            animate('100ms ease-in', style({ opacity: 0 })),
          ]),
        ]),
      ]),
    ]),
  ],
})
export class SearchComponent implements OnChanges, OnInit, OnDestroy {
  private focus = inject(Focus);
  private cdr = inject(ChangeDetectorRef);
  private timeUtils = inject(TimeUtils);
  private platformId = inject(PLATFORM_ID);
  private hostElement = inject(ElementRef<HTMLElement>);
  private renderer = inject(Renderer2);
  private document = inject<Document>(DOCUMENT);
  private userAgent = inject(UserAgent);

  IconSize = IconSize;
  IconColor = IconColor;
  InputType = InputType;
  ListItemSelectedType = ListItemSelectedType;
  expanded = false;
  groupedSearchResults: GroupedSearchResults = {};
  queryControl = new FormControl('', { nonNullable: true });
  focusIndex = -1;
  focusItem?: ListItemComponent<unknown>;
  expandedParentIndex?: number;
  itemTypes: readonly string[] = [];
  groupOffsets: { [type: string]: number } = {};
  searchIconColor: IconColor;
  shortcutHintText = '⌘K';
  expandModifierHintText = 'Ctrl';
  trackByValue = trackByValue;
  private showEmptyResultsForNavigation = false;
  private isSearchKeyboardActive = false;
  private interactionMode?: 'keyboard' | 'mouse';
  /** This is a counter that gets bumped on every navigation action (arrow keys, tab, mouse move, search results change).
   * Deferred callbacks like syncFocusToParent capture a snapshot of the version and return if it has since changed, preventing
   * stale focus updates from overwriting newer ones.
   */
  private focusNavigationVersion = 0;
  private focusElementKey?: string;

  @HostBinding('style.--color-hover-mid')
  get searchResultsHoverMidColor(): string {
    return this.interactionMode === 'keyboard'
      ? 'transparent'
      : 'var(--color-field-focus-outer)';
  }

  @HostBinding('class.KeyboardNavigationMode')
  get keyboardNavigationModeClass(): boolean {
    return this.interactionMode === 'keyboard';
  }

  @Input() disabled = false;
  @Input() isExpandable = false;
  /**
   * Determines the direction from which the search component expands.
   */
  @Input() expandFromDirection = SearchExpandFromDirection.RIGHT;
  @Input() inputSearchSize = InputSize.MID;
  @Input() showShortcutKey = false;
  @Input() showEscHint = false;
  @Input() showNestedStories = false;
  @Input() showEmptyResultsOnKeyboardNavigationOnly = false;
  @Input() leftIconSize = IconSize.MID;
  @Input() itemTypeTitles: { [type: string]: string } = {};
  @Input() itemTemplates: { [type: string]: TemplateRef<any> } = {};
  /** Limits search results' container height and makes it scrollable. */
  @Input() scrollableSearchResults = false;
  /** Max height of the scrollable results container in pixels. */
  @Input() scrollableResultsMaxHeight?: number;

  @HostBinding('style.--scrollable-results-max-height')
  get scrollableResultsMaxHeightStyle(): string | null {
    return this.scrollableResultsMaxHeight
      ? `${this.scrollableResultsMaxHeight}px`
      : null;
  }
  @Input() searchResults: readonly SearchItem[] = [];
  /** Render results in normal flow instead of absolute overlay. */
  @Input() resultsInFlow = false;
  /** Keep existing results card container by default. */
  @Input() useResultsCard = true;
  @Input() placeholder = $localize`Search`;
  @Input() displayResults = true;
  @Input() initialValue = '';
  /** HTML id for the input element. */
  @Input() inputId?: string;
  /**
   * Show options when the user has not entered anything
   */
  @Input() displayResultsWithEmptyInput = false;
  /**
   * Increase the result card z-index to callout level. Default: dropdown.
   */
  @Input() increaseZIndex = false;
  /** Border radius of the results card. */
  @Input() resultsBorderRadius: CardBorderRadius = 'mid';
  /**
   * Not for general use. This is only for Messaging search.
   * If you'd like to use this input, make sure you discuss with UI Eng
   */
  @Input() borderless = false;
  /**
   * Not for general use. This is only for browse mobile page.
   * If you'd like to use this input, make sure you discuss with UI Eng
   */
  @Input() borderlessMobile = false;
  /**
   * Not for general use. This is only for browse mobile page.
   * If you'd like to use this input, make sure you discuss with UI Eng.
   */
  @Input() iconMarginMobile = true;
  @Input() expandableIconLight = false;
  @Input() clearQueryOnSelect = false;

  @Input() showIcon = true;
  @Input() leftIcon: InputIconStart = 'ui-search';

  @Input() roundCorners = true;
  @Input() backgroundColor: InputBackgroundColor = 'default';

  // Toggles the clear button
  @Input() clearButton = true;

  // Additional togglable fields for fl-input
  @Input() dynamicValidation = false;
  @Input() shakeOnBlur = true;

  /**
   * When true, clicking the expandable icon emits onFocus without
   * expanding the inline search bar. Used to hand off to overlay search.
   */
  @Input() preventExpand = false;

  /**
   * When true, renders the search inside a full-screen overlay with a
   * blurred backdrop and top-third positioning.
   */
  @Input() overlayMode = false;

  /**
   * Automatically highlight the first search result when results change.
   */
  @Input() autoHighlightFirst = true;

  /**
   * Show keyboard shortcut hints at the bottom of the overlay.
   * Only visible when overlayMode is active.
   */
  @Input() showKeyboardHints = false;

  // Custom control for use-cases that needs input suggestion
  @Input() control?: FormControl<string>;

  /**
   * Debounces the emission of the search query after the specified number of milliseconds.
   */
  @Input() debounceTimeMs?: number;

  @Output() query = new EventEmitter<string>();
  @Output() select = new EventEmitter<SearchItem>();
  @Output() submit = new EventEmitter<void>();
  @Output() clear = new EventEmitter<void>();
  @Output() onFocus = new EventEmitter<void>();
  @Output() overlayClose = new EventEmitter<void>();
  /**
   * Exposes the expanded/collapsed status of the expandable search bar.
   */
  @Output() expandedChange = new EventEmitter<boolean>();

  // Reference to input component
  @ViewChild('inputComponent')
  inputComponent: InputComponent<any>;
  // Reference to overlay input (only present when overlayMode is true)
  @ViewChild('overlayInputComponent')
  overlayInputComponent?: InputComponent<any>;
  // Reference to list of components
  @ViewChildren('listItem') listItems: QueryList<ListItemComponent<unknown>>;
  // Reference to list of ElementRef<HTMLElement>s
  @ViewChildren('listItem', { read: ElementRef })
  listItemElements: QueryList<ElementRef<HTMLElement>>;

  private querySubscription?: Subscription;

  // Listening for clicks outside the input component
  @HostListener('document:click', ['$event'])
  documentClick(event: MouseEvent): void {
    if (this.overlayMode) {
      return;
    }
    if (
      event &&
      !this.inputComponent.inputContainer.nativeElement.contains(
        event.target as HTMLElement,
      ) &&
      this.expanded
    ) {
      // expanded while there is a query
      this.setExpanded(!!this.queryControl.value);

      this.displayResults = false;
      this.interactionMode = undefined;
      this.setSearchIconColor();
    }
  }

  @HostListener('document:keydown', ['$event'])
  onDocumentKeyDown(event: KeyboardEvent): void {
    if (this.overlayMode && event.key === 'Escape') {
      this.handleOverlayClose();
      return;
    }

    if (!this.isSearchKeyboardActive) {
      return;
    }

    const target = event.target as Node | undefined;
    if (
      target &&
      (this.inputComponent?.inputContainer?.nativeElement.contains(target) ||
        this.overlayInputComponent?.inputContainer?.nativeElement.contains(
          target,
        ))
    ) {
      return;
    }

    const isBodyTarget = target === globalThis.document?.body;
    if (!isBodyTarget && !this.hostElement.nativeElement.contains(target)) {
      return;
    }

    if (this.isSpaceKey(event) && this.focusIndex >= 0) {
      this.interactionMode = 'keyboard';
      event.preventDefault();
      event.stopPropagation();
      this.selectOrSubmit();
      return;
    }

    this.handleNavigationKey(event);
  }

  ngOnInit(): void {
    this.updateShortcutHintText();
    this.subscribeToQueryControl();
    this.setSearchIconColor();
  }

  private updateShortcutHintText(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    const osName = this.userAgent.getUserAgent().getOS().name;
    const isApplePlatform = osName === 'Mac OS' || osName === 'iOS';
    this.shortcutHintText = isApplePlatform ? '⌘K' : 'Ctrl K';
    this.expandModifierHintText = isApplePlatform ? '⌘' : 'Ctrl';
  }

  setSearchIconColor(): void {
    if (!this.isExpandable || this.expanded) {
      this.searchIconColor = IconColor.FOREGROUND;
      return;
    }

    if (this.expandableIconLight) {
      this.searchIconColor = IconColor.LIGHT;
    }
  }

  subscribeToQueryControl(): void {
    this.queryControl = this.control ?? this.queryControl;
    this.querySubscription?.unsubscribe();
    this.querySubscription = this.queryControl.valueChanges
      .pipe(
        this.debounceTimeMs
          ? this.timeUtils.rxDebounceTime(this.debounceTimeMs)
          : query$ => query$,
      )
      .subscribe(searchQuery => {
        this.query.emit(searchQuery);
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if ('overlayMode' in changes) {
      if (this.overlayMode) {
        this.lockBodyScroll();
        queueMicrotask(() => {
          const targetInput = this.overlayInputComponent ?? this.inputComponent;
          this.focus.focusElement(targetInput.nativeElement, {
            allowMobile: true,
          });
          this.isSearchKeyboardActive = true;
        });
      } else {
        this.unlockBodyScroll();
        this.clearQuery();
      }
    }

    this.expandedParentIndex = undefined;

    if ('searchResults' in changes && this.searchResults.length > 0) {
      if (this.autoHighlightFirst) {
        this.focusIndex = 0;
        this.interactionMode = 'keyboard';
        this.focusNavigationVersion += 1;
        this.updateFocusItem();
      } else {
        this.removeFocus();
      }
    } else {
      this.removeFocus();
    }

    if ('debounceTimeMs' in changes) {
      this.subscribeToQueryControl();
    }

    const baseGroupedSearchResults: GroupedSearchResults = { untyped: [] };
    this.groupedSearchResults = this.searchResults.reduce((acc, i) => {
      if (!(i.type in acc)) {
        acc[i.type] = [];
      }
      acc[i.type].push(i);
      return acc;
    }, baseGroupedSearchResults);

    this.itemTypes = Object.keys(this.groupedSearchResults);

    let offset = 0;
    this.groupOffsets = {};
    for (const type of this.itemTypes) {
      this.groupOffsets[type] = offset;
      offset += this.groupedSearchResults[type].length;
    }

    if (this.clearQueryOnSelect) {
      this.clearQuery();
    }

    if ('initialValue' in changes && this.initialValue !== '') {
      this.queryControl.setValue(this.initialValue);
    }
  }

  ngOnDestroy(): void {
    this.querySubscription?.unsubscribe();
    if (this.overlayMode) {
      this.unlockBodyScroll();
    }
  }

  handleIconStartClick(e: MouseEvent): void {
    e.stopPropagation();
    if (this.preventExpand) {
      this.onFocus.emit();
      return;
    }
    this.focus.focusElement(this.inputComponent.nativeElement, {
      allowMobile: true,
    });
    this.displayResults = true;
    this.setExpanded(true);
    this.setSearchIconColor();
    this.showEmptyResultsForNavigation = false;
    this.isSearchKeyboardActive = true;
    this.interactionMode = undefined;
  }

  handleIconClearClick(e: MouseEvent): void {
    e.stopPropagation();
    this.clearQuery();
    this.focus.focusElement(this.inputComponent.nativeElement, {
      allowMobile: true,
    });
  }

  handleOverlayClearClick(e: MouseEvent): void {
    e.stopPropagation();
    this.clearQuery();
    if (this.overlayInputComponent) {
      this.focus.focusElement(this.overlayInputComponent.nativeElement, {
        allowMobile: true,
      });
    }
  }

  handleFocus(): void {
    if (this.queryControl.value.length === 0) {
      this.showEmptyResultsForNavigation = false;
    }
    this.isSearchKeyboardActive = true;
    this.onFocus.emit();
  }

  clearQuery(): void {
    this.queryControl.setValue('');
    this.clear.emit();
    this.showEmptyResultsForNavigation = false;
    this.expandedParentIndex = undefined;
  }

  handleInputKeyDown(e: KeyboardEvent): void {
    if (!e.key) return;

    if (e.key.toLowerCase() === 'enter' && (e.ctrlKey || e.metaKey)) {
      e.preventDefault();
      this.toggleFocusedParentExpansion();
      return;
    }

    this.handleNavigationKey(e);
  }

  handleInputKeyUp(e: KeyboardEvent): void {
    if (!e.key) return;

    const k = e.key.toLowerCase();
    if (k === 'enter') {
      if (e.ctrlKey || e.metaKey) {
        return;
      }
      this.selectOrSubmit();
      return;
    }
    switch (k) {
      case 'escape': {
        // IMPORTANT - if you modify this behaviour make sure
        // to update multi-select. They should offer the same functionality.
        this.clearQuery();
        if (this.overlayMode) {
          this.handleOverlayClose();
        }
        break;
      }
      case 'arrowdown': {
        break;
      }
      case 'arrowup': {
        break;
      }
      case 'tab': {
        break;
      }
      default:
        if (this.queryControl.value.length === 0) {
          this.showEmptyResultsForNavigation = false;
        }
        break; // Do nothing
    }
  }

  selectOrSubmit(): void {
    if (this.focusItem) {
      const focusedElement =
        this.listItemElements.toArray()[this.focusIndex]?.nativeElement;
      const itemKind = focusedElement?.getAttribute('itemKind');
      const itemType = focusedElement?.getAttribute('itemType') as
        | string
        | undefined;
      const itemIndex = Number(focusedElement?.getAttribute('itemIndex'));

      if (!itemType || Number.isNaN(itemIndex)) {
        this.submit.emit();
        return;
      }

      const focusedItem = this.groupedSearchResults[itemType]?.[itemIndex];
      if (!focusedItem) {
        this.submit.emit();
        return;
      }

      if (itemKind === 'nested') {
        const storyIndex = Number(focusedElement?.getAttribute('storyIndex'));
        const story = this.getNestedStories(focusedItem)[storyIndex];
        if (story) {
          this.selectNestedStory(focusedItem, story);
          return;
        }
      }

      if (itemKind === 'parent') {
        this.selectItem(focusedItem);
        return;
      }

      this.select.emit(focusedItem);
    } else {
      this.submit.emit();
    }
  }

  selectItem(item: SearchItem): void {
    this.select.emit(item);
    if (item.displayValue) {
      this.queryControl.setValue(item.displayValue, {
        emitEvent: false,
      });
    } else {
      this.clearQuery();
    }
  }

  toggleFocusedParentExpansion(): void {
    if (!this.focusItem || !this.showNestedStories) {
      return;
    }

    const focusedElement =
      this.listItemElements.toArray()[this.focusIndex]?.nativeElement;
    const itemKind = focusedElement?.getAttribute('itemKind');
    const itemType = focusedElement?.getAttribute('itemType') as
      | string
      | undefined;
    const itemIndex = Number(focusedElement?.getAttribute('itemIndex'));
    const parentIndex = Number(focusedElement?.getAttribute('parentIndex'));

    if (!itemType || Number.isNaN(itemIndex) || Number.isNaN(parentIndex)) {
      return;
    }

    const focusedItem = this.groupedSearchResults[itemType]?.[itemIndex];
    if (!focusedItem || !this.hasNestedStories(focusedItem)) {
      return;
    }

    if (itemKind === 'nested') {
      this.expandedParentIndex = undefined;
      this.syncFocusToParent(parentIndex);
      return;
    }

    if (itemKind === 'parent') {
      this.activateParentItem(focusedItem, parentIndex);
    }
  }

  activateParentItem(item: SearchItem, parentIndex: number): void {
    if (this.showNestedStories && this.hasNestedStories(item)) {
      const isExpanding = this.expandedParentIndex !== parentIndex;
      this.expandedParentIndex = isExpanding ? parentIndex : undefined;
      this.syncFocusToParent(parentIndex);
      if (isExpanding) {
        this.scrollExpandedStoriesIntoView(parentIndex);
      }
      return;
    }

    this.selectItem(item);
  }

  selectNestedStory(
    item: SearchItem,
    story: { title: string; link: string },
  ): void {
    const itemName = this.getItemName(item);
    this.select.emit({
      ...item,
      context: {
        ...(item.context as object),
        name: `${itemName}/${story.title}`,
        link: story.link,
      },
      displayValue: `${itemName}/${story.title}`,
    });
  }

  // Focus helpers - there's quite a few of these, sorry.
  moveDown(): void {
    this.syncFocusIndexFromElementKey();
    this.focusNavigationVersion += 1;
    this.focusIndex += 1;
    this.reFocus();
  }

  moveUp(): void {
    this.syncFocusIndexFromElementKey();
    this.focusNavigationVersion += 1;
    this.focusIndex -= 1;
    this.reFocus();
  }

  mouseMove(item: ListItemComponent<unknown>): void {
    this.interactionMode = 'mouse';
    this.focusNavigationVersion += 1;
    this.focusIndex = this.listItems?.toArray().indexOf(item) ?? -1;
    this.reFocus();
  }

  mouseLeave(): void {
    this.removeFocus();
  }

  reFocus(): void {
    this.fixFocusOverflow();
    this.updateFocusItem();
    this.scrollFocusedItemIntoView();
  }

  updateFocusItem(): void {
    const focusItem = this.listItems?.toArray()[this.focusIndex];
    const focusedElement =
      this.listItemElements?.toArray()[this.focusIndex]?.nativeElement;
    if (focusItem) {
      this.focusItem = focusItem;
      this.focusElementKey = this.getElementKey(focusedElement);
    } else {
      delete this.focusItem;
      this.focusElementKey = undefined;
    }
    this.cdr.markForCheck();
  }

  removeFocus(): void {
    delete this.focusItem;
    this.focusIndex = -1;
    this.focusElementKey = undefined;
    this.interactionMode = undefined;
    this.cdr.markForCheck();
  }

  fixFocusOverflow(): void {
    const resultsLength =
      this.listItemElements?.length ?? this.searchResults.length;
    if (resultsLength === 0) {
      this.focusIndex = -1;
      return;
    }

    if (this.focusIndex < 0) {
      this.focusIndex = resultsLength - 1;
    } else if (this.focusIndex >= resultsLength) {
      this.focusIndex = 0;
    }
  }

  scrollFocusedItemIntoView(): void {
    const element = this.listItemElements?.toArray()[this.focusIndex];
    if (element) {
      element.nativeElement.scrollIntoView({ block: 'nearest' });
    }
  }

  trackBySearchItem(_: number, item: SearchItem): SearchItem {
    return item;
  }

  hasNestedStories(item: SearchItem): boolean {
    return Array.isArray(item.context?.stories);
  }

  getItemName(item: SearchItem): string {
    return item.context?.name || item.displayValue || '';
  }

  getNestedStories(
    item: SearchItem,
  ): readonly { title: string; link: string }[] {
    return item.context?.stories ?? [];
  }

  isParentExpanded(parentIndex: number): boolean {
    return this.expandedParentIndex === parentIndex;
  }

  isFocusedItemInExpandedGroup(): boolean {
    if (this.expandedParentIndex === undefined) {
      return false;
    }
    const focusedElement =
      this.listItemElements?.toArray()[this.focusIndex]?.nativeElement;
    if (!focusedElement) {
      return false;
    }
    return (
      Number(focusedElement.getAttribute('parentIndex')) ===
      this.expandedParentIndex
    );
  }

  isParentSelected(parentIndex: number): boolean {
    const focusedElement =
      this.listItemElements?.toArray()[this.focusIndex]?.nativeElement;
    if (
      !focusedElement ||
      focusedElement.getAttribute('itemKind') !== 'parent'
    ) {
      return false;
    }

    return Number(focusedElement.getAttribute('parentIndex')) === parentIndex;
  }

  isNestedSelected(
    type: string,
    itemIndex: number,
    storyIndex: number,
  ): boolean {
    const focusedElement =
      this.listItemElements?.toArray()[this.focusIndex]?.nativeElement;
    if (
      !focusedElement ||
      focusedElement.getAttribute('itemKind') !== 'nested'
    ) {
      return false;
    }

    return (
      focusedElement.getAttribute('itemType') === type &&
      Number(focusedElement.getAttribute('itemIndex')) === itemIndex &&
      Number(focusedElement.getAttribute('storyIndex')) === storyIndex
    );
  }

  showResultsPanel(): boolean {
    if (!this.displayResults) {
      return false;
    }

    if (this.queryControl.value) {
      return true;
    }

    if (!this.displayResultsWithEmptyInput) {
      return false;
    }

    if (!this.showEmptyResultsOnKeyboardNavigationOnly) {
      return true;
    }

    return this.showEmptyResultsForNavigation;
  }

  private enableEmptyResultsForKeyboardNavigation(): boolean {
    if (
      !this.showEmptyResultsOnKeyboardNavigationOnly ||
      this.queryControl.value.length > 0 ||
      this.showEmptyResultsForNavigation
    ) {
      return false;
    }

    this.showEmptyResultsForNavigation = true;
    this.cdr.markForCheck();
    return true;
  }

  private handleNavigationKey(e: KeyboardEvent): void {
    const k = e.key.toLowerCase();
    const { shiftKey } = e;
    if (k !== 'arrowdown' && k !== 'arrowup' && k !== 'tab') {
      return;
    }

    this.interactionMode = 'keyboard';

    this.enableEmptyResultsForKeyboardNavigation();

    if (k === 'arrowdown' || (k === 'tab' && !shiftKey)) {
      this.moveDown();
    } else {
      this.moveUp();
    }

    e.stopPropagation();
    e.preventDefault();
  }

  private syncFocusToParent(parentIndex: number): void {
    const scheduledVersion = this.focusNavigationVersion;
    this.cdr.markForCheck();
    setTimeout(() => {
      if (scheduledVersion !== this.focusNavigationVersion) {
        return;
      }

      const parentRowIndex =
        this.listItemElements?.toArray().findIndex(({ nativeElement }) => {
          return (
            nativeElement.getAttribute('itemKind') === 'parent' &&
            Number(nativeElement.getAttribute('parentIndex')) === parentIndex
          );
        }) ?? -1;

      if (parentRowIndex < 0) {
        return;
      }

      this.focusIndex = parentRowIndex;
      this.updateFocusItem();
      this.scrollFocusedItemIntoView();
    });
  }

  private scrollExpandedStoriesIntoView(parentIndex: number): void {
    this.cdr.markForCheck();
    setTimeout(() => {
      const resultElements = this.listItemElements
        ?.toArray()
        .map(element => element.nativeElement);

      if (!resultElements || resultElements.length === 0) {
        return;
      }

      const parentElement = resultElements.find(
        element =>
          element.getAttribute('itemKind') === 'parent' &&
          Number(element.getAttribute('parentIndex')) === parentIndex,
      );
      if (!parentElement) {
        return;
      }

      // Keep expanded parent anchored at the top of the results viewport.
      parentElement.scrollIntoView({ block: 'start' });
    });
  }

  private isSpaceKey(e: KeyboardEvent): boolean {
    return e.key === ' ' || e.key === 'Spacebar';
  }

  private syncFocusIndexFromElementKey(): void {
    if (!this.focusElementKey) {
      return;
    }

    const elements = this.listItemElements?.toArray() ?? [];
    if (elements.length === 0) {
      return;
    }

    const currentKey = this.getElementKey(
      elements[this.focusIndex]?.nativeElement,
    );
    if (currentKey === this.focusElementKey) {
      return;
    }

    const remappedIndex = elements.findIndex(
      element =>
        this.getElementKey(element.nativeElement) === this.focusElementKey,
    );
    if (remappedIndex !== -1) {
      this.focusIndex = remappedIndex;
    }
  }

  private getElementKey(element: HTMLElement | undefined): string | undefined {
    if (!element) {
      return undefined;
    }

    return [
      element.getAttribute('itemKind') ?? '',
      element.getAttribute('itemType') ?? '',
      element.getAttribute('itemIndex') ?? '',
      element.getAttribute('storyIndex') ?? '',
      element.getAttribute('parentIndex') ?? '',
    ].join('|');
  }

  handleOverlayClose(): void {
    this.overlayClose.emit();
  }

  private lockBodyScroll(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }
    this.renderer.setStyle(this.document.body, 'overflow', 'hidden');
  }

  private unlockBodyScroll(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }
    this.renderer.removeStyle(this.document.body, 'overflow');
  }

  // T252371 - Set the query value on selection of an item in
  // a custom (external) search results list
  setQuery(value: string): void {
    this.queryControl.setValue(value);
  }

  private setExpanded(expanded: boolean): void {
    this.expanded = expanded;
    this.expandedChange.emit(expanded);
  }
}
